<template>
  <div v-if="active" ref="container" class="help-assistant" :class="{ 'help-assistant--dark': $vuetify.theme.dark }" :style="containerPosition">
    <transition name="assistant-panel">
      <section v-if="open" ref="window" class="assistant-window" :style="windowPosition" aria-label="Help assistant">
        <header class="assistant-header" @pointerdown="startDrag">
          <span class="assistant-avatar"><v-icon>mdi-robot-outline</v-icon></span>
          <div class="assistant-header-text"><strong>{{ assistantName }}</strong><small>Ask about accounts, balances, KYC, and more...</small></div>
          <button type="button" aria-label="Start a new conversation" @click="resetConversation"><v-icon>mdi-refresh</v-icon></button>
          <button type="button" aria-label="Collapse assistant" @click="open = false"><v-icon>mdi-chevron-down</v-icon></button>
        </header>

        <div ref="messages" class="assistant-messages">
          <div v-if="!messages.length" class="assistant-welcome">
            <p>{{ greeting }} <span aria-hidden="true">👋</span> I can show your trading accounts, balances, verification details, or walk you through a task. Try a suggestion below.</p>
          </div>
          <div v-for="item in messages" :key="item.key" class="assistant-message" :class="`assistant-message--${item.role}`">
            <div class="assistant-bubble" v-html="formatText(item.text)"></div>
            <div v-if="item.card && item.card.card_type === 'list'" class="assistant-list-card">
              <strong>{{ item.card.title }}</strong>
              <small>{{ item.card.subtitle }}</small>
              <div v-if="item.card.rows && item.card.rows.length" class="assistant-list-rows">
                <div v-for="(row, index) in item.card.rows" :key="`${item.key}-${index}`" class="assistant-list-row">
                  <div><strong>{{ row.title }}</strong><span>{{ row.sub }}</span></div>
                  <b>{{ row.right }}</b>
                </div>
              </div>
              <p v-else>{{ item.card.empty }}</p>
              <button v-if="item.card.deep_link" type="button" class="assistant-list-cta" @click="openRoute(item.card.deep_link)">
                {{ item.card.cta || "Open accounts" }} <v-icon size="15">mdi-arrow-right</v-icon>
              </button>
            </div>
            <div v-if="item.card && item.card.page_routes" class="assistant-actions">
              <button v-for="link in item.card.page_routes" :key="`${link.route}-${link.label}`" type="button" @click="handleAssistantAction(link)">
                {{ link.label || "Open page" }} <v-icon size="15">mdi-arrow-right</v-icon>
              </button>
            </div>
          </div>
          <div v-if="loading" class="assistant-message assistant-message--assistant"><div class="assistant-bubble assistant-typing">● ● ●</div></div>
        </div>

        <div v-if="suggestions.length" class="assistant-suggestion-area" :class="{ 'assistant-suggestion-area--inline': messages.length }">
          <small>{{ messages.length ? "SUGGESTED FOR THIS PAGE" : "TRY ASKING" }} <v-icon size="13">mdi-menu-down</v-icon></small>
          <div class="assistant-suggestions">
            <button v-for="suggestion in suggestions" :key="suggestion.id || suggestion.label" type="button" @click="ask(suggestion.prompt_text)">
              <span aria-hidden="true">✦</span> {{ suggestion.label }}
            </button>
          </div>
        </div>

        <form class="assistant-composer" @submit.prevent="ask(draft)">
          <v-icon class="assistant-composer-icon" size="18">mdi-volume-off</v-icon>
          <textarea
            v-model.trim="draft"
            rows="1"
            maxlength="1000"
            :placeholder="listening ? 'Listening…' : 'Ask a question…'"
            @keydown.enter.exact.prevent="ask(draft)"
            @keydown.up.prevent="navigateHistory(-1)"
            @keydown.down.prevent="navigateHistory(1)"
          ></textarea>
          <button
            v-if="speechSupported"
            type="button"
            class="assistant-mic-button"
            :class="{ 'assistant-mic-button--active': listening }"
            :aria-label="listening ? 'Stop voice search' : 'Ask by voice'"
            @click="toggleVoiceInput"
          >
            <v-icon class="assistant-composer-icon" size="19">{{ listening ? "mdi-microphone" : "mdi-microphone-outline" }}</v-icon>
          </button>
          <button type="submit" :disabled="!draft || loading" aria-label="Send question"><v-icon>mdi-send</v-icon></button>
        </form>
      </section>
    </transition>

    <button
      class="assistant-fab"
      type="button"
      :aria-label="open ? 'Close help assistant' : 'Open help assistant'"
      @pointerdown="startFabDrag"
      @click="handleFabClick"
    >
      <v-icon>{{ open ? "mdi-close" : "mdi-headset" }}</v-icon>
    </button>
  </div>
</template>

<script>
import HTTP from "@/_helpers/http";

export default {
  name: "HelpAssistantWidget",
  data() {
    return {
      active: false,
      open: false,
      loading: false,
      draft: "",
      assistantName: "CRM Help",
      conversationId: null,
      suggestions: [],
      messages: [],
      searchHistory: [],
      historyIndex: 0,
      draftBeforeHistory: "",
      windowPosition: null,
      drag: null,
      containerPosition: null,
      fabDrag: null,
      fabDragged: false,
      listening: false,
      speechSupported: false,
      recognition: null,
    };
  },
  watch: {
    "$route.fullPath"() {
      this.loadConfig();
    },
  },
  mounted() {
    this.loadConfig();
    this.initSpeechRecognition();
    window.addEventListener("resize", this.keepWindowInView);
    window.addEventListener("resize", this.keepContainerInView);
  },
  beforeDestroy() {
    this.stopDrag();
    this.stopFabDrag();
    this.stopVoiceInput();
    window.removeEventListener("resize", this.keepWindowInView);
    window.removeEventListener("resize", this.keepContainerInView);
  },
  computed: {
    greeting() {
      const hour = new Date().getHours();
      if (hour < 12) return "Good morning!";
      if (hour < 18) return "Good afternoon!";
      return "Good evening!";
    },
  },
  methods: {
    startDrag(event) {
      if (event.button !== 0 || event.target.closest("button")) return;
      const bounds = this.$refs.window.getBoundingClientRect();
      this.drag = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
      this.windowPosition = { left: `${bounds.left}px`, top: `${bounds.top}px`, right: "auto", bottom: "auto" };
      window.addEventListener("pointermove", this.moveDrag);
      window.addEventListener("pointerup", this.stopDrag);
      window.addEventListener("pointercancel", this.stopDrag);
      event.preventDefault();
    },
    moveDrag(event) {
      if (!this.drag || !this.$refs.window) return;
      const bounds = this.$refs.window.getBoundingClientRect();
      this.setWindowPosition(event.clientX - this.drag.x, event.clientY - this.drag.y, bounds);
    },
    setWindowPosition(left, top, bounds) {
      this.windowPosition = {
        left: `${Math.max(0, Math.min(left, window.innerWidth - bounds.width))}px`,
        top: `${Math.max(0, Math.min(top, window.innerHeight - bounds.height))}px`,
        right: "auto",
        bottom: "auto",
      };
    },
    keepWindowInView() {
      if (!this.windowPosition || !this.$refs.window) return;
      const bounds = this.$refs.window.getBoundingClientRect();
      this.setWindowPosition(bounds.left, bounds.top, bounds);
    },
    stopDrag() {
      this.drag = null;
      window.removeEventListener("pointermove", this.moveDrag);
      window.removeEventListener("pointerup", this.stopDrag);
      window.removeEventListener("pointercancel", this.stopDrag);
    },
    startFabDrag(event) {
      if (event.button !== 0 || !this.$refs.container) return;
      const bounds = this.$refs.container.getBoundingClientRect();
      this.fabDrag = {
        offsetX: event.clientX - bounds.left,
        offsetY: event.clientY - bounds.top,
        startX: event.clientX,
        startY: event.clientY,
      };
      window.addEventListener("pointermove", this.moveFabDrag);
      window.addEventListener("pointerup", this.stopFabDrag);
      window.addEventListener("pointercancel", this.stopFabDrag);
    },
    moveFabDrag(event) {
      if (!this.fabDrag || !this.$refs.container) return;
      const dx = event.clientX - this.fabDrag.startX;
      const dy = event.clientY - this.fabDrag.startY;
      if (!this.fabDragged && Math.hypot(dx, dy) > 4) this.fabDragged = true;
      if (!this.fabDragged) return;
      const bounds = this.$refs.container.getBoundingClientRect();
      this.setContainerPosition(event.clientX - this.fabDrag.offsetX, event.clientY - this.fabDrag.offsetY, bounds);
    },
    setContainerPosition(left, top, bounds) {
      this.containerPosition = {
        left: `${Math.max(0, Math.min(left, window.innerWidth - bounds.width))}px`,
        top: `${Math.max(0, Math.min(top, window.innerHeight - bounds.height))}px`,
        right: "auto",
        bottom: "auto",
      };
    },
    keepContainerInView() {
      if (!this.containerPosition || !this.$refs.container) return;
      const bounds = this.$refs.container.getBoundingClientRect();
      this.setContainerPosition(bounds.left, bounds.top, bounds);
    },
    stopFabDrag() {
      this.fabDrag = null;
      window.removeEventListener("pointermove", this.moveFabDrag);
      window.removeEventListener("pointerup", this.stopFabDrag);
      window.removeEventListener("pointercancel", this.stopFabDrag);
    },
    handleFabClick() {
      if (this.fabDragged) {
        this.fabDragged = false;
        return;
      }
      this.open = !this.open;
    },
    initSpeechRecognition() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        this.speechSupported = false;
        return;
      }
      this.speechSupported = true;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = true;
      this.recognition.lang = navigator.language || "en-US";
      this.recognition.onresult = (event) => {
        let transcript = "";
        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          transcript += event.results[index][0].transcript;
        }
        this.draft = transcript;
        const lastResult = event.results[event.results.length - 1];
        if (lastResult && lastResult.isFinal) {
          this.listening = false;
          this.ask(transcript);
        }
      };
      this.recognition.onerror = (event) => {
        this.listening = false;
        if (event.error === "not-allowed" || event.error === "service-not-allowed") {
          this.messages.push({
            key: `error-${Date.now()}`,
            role: "assistant",
            text: "I couldn't access your microphone. Please allow microphone access and try again.",
          });
          this.scrollToEnd();
        }
      };
      this.recognition.onend = () => {
        this.listening = false;
      };
    },
    toggleVoiceInput() {
      if (!this.speechSupported || !this.recognition) return;
      if (this.listening) {
        this.recognition.stop();
        return;
      }
      this.draft = "";
      this.listening = true;
      try {
        this.recognition.start();
      } catch (error) {
        this.listening = false;
      }
    },
    stopVoiceInput() {
      if (!this.recognition) return;
      this.recognition.onresult = null;
      this.recognition.onerror = null;
      this.recognition.onend = null;
      try {
        this.recognition.stop();
      } catch (error) {
        // no active recognition session to stop
      }
    },
    async loadConfig() {
      if (this.$route.meta.noSidebar) {
        this.active = false;
        return;
      }
      try {
        const response = await HTTP().get("/assistant/config", { params: { route: this.$route.path } });
        this.active = Boolean(response.data.active);
        this.assistantName = response.data.name || "CRM Help";
        this.suggestions = response.data.suggestions || [];
      } catch (error) {
        this.active = false;
      }
    },
    async ask(question) {
      const message = String(question || "").trim();
      if (!message || this.loading) return;
      if (this.searchHistory[this.searchHistory.length - 1] !== message) {
        this.searchHistory.push(message);
      }
      this.historyIndex = this.searchHistory.length;
      this.draftBeforeHistory = "";
      this.messages.push({ key: `user-${Date.now()}`, role: "user", text: message });
      this.draft = "";
      this.loading = true;
      this.scrollToEnd();
      try {
        const response = await HTTP().post("/assistant/ask", {
          message,
          conversation_id: this.conversationId,
          page_route: this.$route.fullPath,
        });
        this.conversationId = response.data.conversation_id || this.conversationId;
        const reply = response.data.reply || { text: "Sorry, I could not answer that question." };
        this.messages.push({ key: `assistant-${Date.now()}`, role: "assistant", text: reply.text, card: reply.card });
      } catch (error) {
        const payload = error.response && error.response.data ? error.response.data : {};
        const detail = payload.error || payload.message;
        const reference = payload.error_id ? ` Reference: ${payload.error_id}` : "";
        const text = detail
          ? `I couldn't complete that request: ${detail}.${reference}`
          : `The help assistant is temporarily unavailable.${reference}`;
        console.error("Help assistant request failed", {
          status: error.response && error.response.status,
          payload,
          error,
        });
        this.messages.push({ key: `error-${Date.now()}`, role: "assistant", text });
      } finally {
        this.loading = false;
        this.scrollToEnd();
      }
    },
    navigateHistory(direction) {
      if (!this.searchHistory.length) return;
      if (this.historyIndex === this.searchHistory.length && direction < 0) {
        this.draftBeforeHistory = this.draft;
      }
      const nextIndex = Math.max(0, Math.min(this.searchHistory.length, this.historyIndex + direction));
      if (nextIndex === this.historyIndex) return;
      this.historyIndex = nextIndex;
      this.draft = nextIndex === this.searchHistory.length
        ? this.draftBeforeHistory
        : this.searchHistory[nextIndex];
    },
    resetConversation() {
      this.conversationId = null;
      this.messages = [];
      this.draft = "";
      this.historyIndex = this.searchHistory.length;
      this.draftBeforeHistory = "";
    },
    formatText(value) {
      return String(value || "")
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br>");
    },
    openRoute(route) {
      if (!route) return;
      this.open = false;
      this.$router.push(route).catch(error => {
        if (error.name !== "NavigationDuplicated") throw error;
      });
    },
    async handleAssistantAction(link) {
      if (!link) return;
      if (link.prompt) {
        await this.ask(link.prompt);
        if (link.route) {
          await this.$router.push(link.route).catch(error => {
            if (error.name !== "NavigationDuplicated") throw error;
          });
        }
        this.open = true;
        this.scrollToEnd();
        return;
      }
      this.openRoute(link.route);
    },
    scrollToEnd() {
      this.$nextTick(() => {
        if (this.$refs.messages) this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.help-assistant {
  --assistant-panel: #fff;
  --assistant-soft: #f4f7fb;
  --assistant-border: #dce5ef;
  --assistant-text: #172033;
  --assistant-muted: #718096;
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 210;
}
.help-assistant--dark { --assistant-panel: #0b1b2e; --assistant-soft: #10243b; --assistant-border: #243b56; --assistant-text: #f3f7fc; --assistant-muted: #91a4bd; }
.assistant-fab, .assistant-avatar { display: inline-flex; align-items: center; justify-content: center; border: 0; border-radius: 50%; background: #1e88e5; color: #fff; }
.assistant-fab { width: 56px; height: 56px; margin-left: auto; box-shadow: 0 10px 28px rgba(30,136,229,.35); cursor: grab; touch-action: none; user-select: none; }
.assistant-fab:active { cursor: grabbing; }
.assistant-fab .v-icon, .assistant-avatar .v-icon { color: #fff !important; }
.assistant-window { position: absolute; right: 0; bottom: 70px; display: flex; width: min(460px, calc(100vw - 32px)); height: min(620px, calc(100vh - 120px)); flex-direction: column; overflow: hidden; border: 1px solid var(--assistant-border); border-radius: 16px; background: var(--assistant-panel); box-shadow: 0 20px 60px rgba(0,0,0,.25); color: var(--assistant-text); }
.assistant-header { display: flex; min-height: 68px; padding: 12px 16px; align-items: center; gap: 11px; border-bottom: 1px solid var(--assistant-border); }
.assistant-avatar { width: 38px; height: 38px; flex: 0 0 38px; }
.assistant-avatar--large { width: 58px; height: 58px; }
.assistant-header > div { display: flex; flex-direction: column; }
.assistant-header small { color: var(--assistant-muted); font-size: 11px; }
.assistant-header button { border: 0; background: transparent; cursor: pointer; }
.assistant-header button .v-icon { color: var(--assistant-muted) !important; }
.assistant-messages { flex: 1; padding: 18px; overflow-y: auto; }
.assistant-welcome { display: flex; padding: 35px 20px 18px; flex-direction: column; align-items: center; text-align: center; gap: 8px; }
.assistant-welcome p { max-width: 310px; margin: 0; color: var(--assistant-muted); font-size: 13px; }
.assistant-message { display: flex; margin-bottom: 12px; flex-direction: column; align-items: flex-start; }
.assistant-message--user { align-items: flex-end; }
.assistant-bubble { max-width: 86%; padding: 10px 13px; border-radius: 12px 12px 12px 3px; background: var(--assistant-soft); color: var(--assistant-text); font-size: 13px; line-height: 1.55; }
.assistant-message--user .assistant-bubble { border-radius: 12px 12px 3px 12px; background: #1e88e5; color: #fff; }
.assistant-typing { color: var(--assistant-muted); letter-spacing: 3px; }
.assistant-actions button { margin-top: 7px; padding: 7px 10px; border: 1px solid #1e88e5; border-radius: 7px; background: transparent; color: #1e88e5; font-size: 12px; cursor: pointer; }
.assistant-actions .v-icon { color: inherit !important; }
.assistant-list-card { width: 94%; margin-top: 7px; padding: 12px; border: 1px solid var(--assistant-border); border-radius: 11px; background: var(--assistant-soft); }
.assistant-list-card > strong { display: block; color: var(--assistant-text); font-size: 14px; }
.assistant-list-card > small { display: block; margin-top: 2px; color: var(--assistant-muted); font-size: 11px; }
.assistant-list-rows { display: grid; gap: 8px; margin-top: 10px; }
.assistant-list-row { display: flex; padding-top: 8px; align-items: flex-start; justify-content: space-between; gap: 10px; border-top: 1px solid var(--assistant-border); }
.assistant-list-row > div { display: flex; min-width: 0; flex-direction: column; gap: 2px; }
.assistant-list-row strong { color: var(--assistant-text); font-size: 12px; }
.assistant-list-row span { color: var(--assistant-muted); font-size: 10px; line-height: 1.4; }
.assistant-list-row b { flex: 0 0 auto; color: #19a974; font-size: 10px; }
.assistant-list-card > p { margin: 10px 0 0; color: var(--assistant-muted); font-size: 12px; }
.assistant-list-cta { display: flex; width: 100%; margin-top: 10px; padding: 8px; align-items: center; justify-content: center; gap: 5px; border: 1px solid #1e88e5; border-radius: 7px; background: transparent; color: #1e88e5; font-size: 11px; cursor: pointer; }
.assistant-list-cta .v-icon { color: inherit !important; }
.assistant-suggestions { display: flex; padding: 0 16px 12px; flex-wrap: wrap; gap: 7px; }
.assistant-suggestions button { padding: 7px 10px; border: 0; border-radius: 20px; background: linear-gradient(135deg, rgba(30,136,229,.16), rgba(118,40,220,.16)); color: var(--assistant-text); font-size: 11px; cursor: pointer; }
.assistant-composer { display: flex; margin: 0 14px 14px; padding: 8px 8px 8px 12px; align-items: flex-end; gap: 8px; border: 1px solid var(--assistant-border); border-radius: 12px; background: var(--assistant-soft); }
.assistant-composer:focus-within { border-color: #1e88e5; box-shadow: 0 0 0 2px rgba(30,136,229,.12); }
.assistant-composer textarea { min-height: 32px; max-height: 90px; flex: 1; resize: none; border: 0; outline: 0; background: transparent; color: var(--assistant-text); font: inherit; font-size: 13px; }
.assistant-composer button:not(.assistant-mic-button) { width: 34px; height: 34px; border: 0; border-radius: 9px; background: #1e88e5; cursor: pointer; }
.assistant-composer button:disabled { opacity: .45; cursor: default; }
.assistant-composer .v-icon { color: #fff !important; font-size: 18px; }
.assistant-mic-button { display: inline-flex; flex: 0 0 auto; align-items: center; justify-content: center; border: 0; background: transparent; cursor: pointer; }
.assistant-mic-button--active .assistant-composer-icon { color: #e5393b !important; animation: assistant-mic-pulse 1.1s ease-in-out infinite; }
@keyframes assistant-mic-pulse { 0%, 100% { opacity: 1; } 50% { opacity: .4; } }
.assistant-panel-enter-active, .assistant-panel-leave-active { transition: opacity .18s ease, transform .18s ease; }
.assistant-panel-enter, .assistant-panel-leave-to { opacity: 0; transform: translateY(10px) scale(.98); }

/* Compact help-assistant layout */
.assistant-window { width: min(500px, calc(100vw - 32px)); height: min(650px, calc(100vh - 105px)); border: 0; border-radius: 20px; background: #f4f7fb; box-shadow: 0 18px 42px rgba(25, 49, 76, .3); }
.assistant-header { min-height: 68px; padding: 10px 18px; gap: 10px; border: 0; background: linear-gradient(135deg, #258ce2, #0872cb); color: #fff; }
.assistant-header .assistant-avatar { width: 42px; height: 42px; flex-basis: 42px; border: 1px solid rgba(255,255,255,.35); background: rgba(255,255,255,.12); }
.assistant-header > div { min-width: 0; }
.assistant-header strong { color: #fff; font-size: 16px; line-height: 1.25; }
.assistant-header small { overflow: hidden; color: rgba(255,255,255,.9); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.assistant-header button { display: inline-flex; width: 30px; height: 30px; padding: 0; align-items: center; justify-content: center; border-radius: 50%; }
.assistant-header button:hover { background: rgba(255,255,255,.14); }
.assistant-header button .v-icon { color: #fff !important; font-size: 21px; }
.assistant-messages { padding: 15px; }
.assistant-welcome { display: block; padding: 0; text-align: left; }
.assistant-welcome p { max-width: none; margin: 0; padding: 13px 15px; border: 1px solid #e2e8f0; border-radius: 17px 17px 17px 5px; background: #fff; box-shadow: 0 2px 4px rgba(35, 55, 80, .08); color: #27303f; font-size: 14px; line-height: 1.55; }
.assistant-message { margin-bottom: 11px; }
.assistant-bubble { max-width: 90%; border-radius: 16px 16px 16px 5px; background: #fff; box-shadow: 0 1px 3px rgba(35,55,80,.06); font-size: 13px; }
.assistant-message--user .assistant-bubble { border-radius: 16px 16px 5px 16px; background: #2189e5; }
.assistant-list-card { width: 96%; border-color: #d5e1ee; border-radius: 14px; background: #fff; }
.assistant-suggestion-area { padding: 0 15px 10px; }
.assistant-suggestion-area--inline { padding: 10px 15px; border-top: 1px solid var(--assistant-border); }
.assistant-suggestion-area > small { display: flex; margin-bottom: 7px; align-items: center; color: #8296bc; font-size: 10px; font-weight: 700; letter-spacing: .06em; }
.assistant-suggestion-area > small .v-icon { color: #8296bc !important; }
.assistant-suggestions { padding: 0; gap: 6px; }
.assistant-suggestions button { padding: 6px 12px; border: 1px solid #acd5fb; border-radius: 18px; background: #eaf5ff; color: #2464a5; font-size: 11px; font-weight: 600; line-height: 1.2; }
.assistant-suggestions button:hover { border-color: #258ce2; background: #dcedff; }
.assistant-suggestions button span { margin-right: 2px; color: #4a91cf; }
.assistant-composer { min-height: 54px; margin: 0 15px 15px; padding: 5px 6px 5px 13px; align-items: center; border: 1px solid #cddbea; border-radius: 28px; background: #fff; }
.assistant-composer textarea { min-height: 24px; padding: 5px 3px; color: #27303f; font-size: 13px; line-height: 22px; }
.assistant-composer textarea::placeholder { color: #9aa9bd; }
.assistant-composer .assistant-composer-icon { flex: 0 0 auto; color: #8293a9 !important; }
.assistant-composer button:not(.assistant-mic-button) { width: 42px; height: 42px; flex: 0 0 42px; border-radius: 50%; background: #238de7; }
.assistant-composer button:disabled { background: #a9cae8; opacity: 1; }
.assistant-composer button:not(.assistant-mic-button) .v-icon { font-size: 21px; }
.assistant-mic-button { width: 34px; height: 34px; }
.assistant-mic-button--active { background: rgba(229, 57, 59, .1); border-radius: 50%; }
.help-assistant--dark .assistant-window { background: #0b1b2e; }
.help-assistant--dark .assistant-welcome p, .help-assistant--dark .assistant-bubble, .help-assistant--dark .assistant-list-card, .help-assistant--dark .assistant-composer { border-color: #243b56; background: #10243b; color: #f3f7fc; }
.help-assistant--dark .assistant-composer textarea { color: #f3f7fc; caret-color: #f3f7fc; }
.help-assistant--dark .assistant-composer textarea::placeholder { color: #91a4bd; opacity: 1; }
@media (max-width: 599px) { .help-assistant { right: 16px; bottom: 16px; } .assistant-window { bottom: 66px; height: min(570px, calc(100vh - 100px)); } }
</style>
